import { Box } from "@/components/ui/box";
import { HStack } from "@/components/ui/hstack";
import { Text } from "@/components/ui/text";
import { Icon } from "@/components/ui/icon";
import { ScrollView } from "@/components/ui/scroll-view";
import { Spinner } from "@/components/ui/spinner";
import { VStack } from "@/components/ui/vstack";
import {
  useOrganizationControllerFindOne,
  useUserControllerMe,
} from "@/generated/api/api";
import { EmptyState } from "@/screens/common/empty.state";
import { RestaurantSection } from "@/screens/restaurants/details/restaurant.section";
import {
  RestaurantTeamListMini,
  RestaurantTeamListMiniRef,
} from "@/screens/restaurants/details/restaurant.team.list";
import {
  RestaurantTransactionListMini,
  RestaurantTransactionListMiniRef,
} from "@/screens/restaurants/details/restaurant.transaction.list";
import {
  RestaurantWalletListMini,
  RestaurantWalletListMiniRef,
} from "@/screens/restaurants/details/restaurant.wallet.list";
import { RestaurantBanner } from "@/screens/restaurants/restaurant.banner";
import { Stack, Link as ExpoLink } from "expo-router";
import { useLocalSearchParams, useRouter } from "expo-router/build/hooks";
import {
  ChevronRight,
  DoorOpen,
  HandCoins,
  LucideIcon,
  QrCode,
} from "lucide-react-native";
import { useRef, useState } from "react";
import { RefreshControl } from "react-native";
import { Divider } from "@/components/ui/divider";
import { Card } from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";
import { useMyParticipant } from "@/utils/useMyParticipant";
import { Pressable } from "@/components/ui/pressable";
import { RestaurantTeamModalDelete } from "@/screens/restaurants/details/restaurant.team.modal";

function RestaurantSettingsItem({
  icon,
  title,
  subtitle,
  type,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  type?: "NORMAL" | "DESTRUCTIVE";
}) {
  return (
    <Card
      size="sm"
      variant="ghost"
      className="flex-row justify-between w-full items-center"
    >
      <HStack className="items-center" space="md">
        <Icon as={icon} size={25} className="mr-2" />
        <VStack>
          <Heading
            size="xs"
            className={`text-ellipsis overflow-hidden whitespace-nowrap font-medium ${
              type === "DESTRUCTIVE" ? "text-red-500" : ""
            }`}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {title}
          </Heading>
          {subtitle && <Text size="xs">{subtitle}</Text>}
        </VStack>
      </HStack>
      <Icon as={ChevronRight} />
    </Card>
  );
}

export default function RestaurantPage() {
  const { orgId } = useLocalSearchParams<{ orgId: string }>();

  const { data, isLoading, isLoadingError, isRefetching, refetch } =
    useOrganizationControllerFindOne(orgId);

  const walletsRef = useRef<RestaurantWalletListMiniRef>(null);
  const transactionRef = useRef<RestaurantTransactionListMiniRef>(null);
  const teamListRef = useRef<RestaurantTeamListMiniRef>(null);
  const [walletsCount, setWalletsCount] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);
  const [teammatesCount, setTeammatesCount] = useState(0);
  const [deleteIsOpened, setDeleteIsOpened] = useState(false);
  const { myParticipant, refetch: refetchMyParticipant } =
    useMyParticipant(orgId);

  const { data: user } = useUserControllerMe();
  const hasRW =
    myParticipant?.role === "ADMIN" || myParticipant?.role === "OWNER";

  const router = useRouter();

  return (
    <>
      <Stack.Screen
        options={{
          title: data?.name ?? "Business",
          headerRight: hasRW
            ? () => (
                <ExpoLink
                  href={{
                    pathname: "/(tabs)/orgs/[orgId]/qrcodes",
                    params: {
                      orgId,
                    },
                  }}
                >
                  <Box className="mr-4 flex-1">
                    <Icon as={QrCode} size={25} />
                  </Box>
                </ExpoLink>
              )
            : undefined,
        }}
      />
      <Box className="w-full h-full">
        <ScrollView
          contentContainerClassName="px-4 pb-4 pt-4 grow"
          showsVerticalScrollIndicator={false}
          className="flex-1"
          refreshControl={
            <RefreshControl
              refreshing={isLoading || isRefetching}
              onRefresh={() => {
                refetch();
                refetchMyParticipant();
                walletsRef.current?.refresh();
                transactionRef.current?.refresh();
                teamListRef.current?.refresh();
              }}
            />
          }
        >
          {data ? (
            <VStack className="h-full w-full gap-6">
              <RestaurantBanner organization={data} />
              {hasRW && (
                <RestaurantSection
                  title="Wallets"
                  seeAll={
                    walletsCount > 2
                      ? {
                          href: {
                            pathname: "/(tabs)/orgs/[orgId]/wallets",
                            params: { orgId },
                          },
                        }
                      : undefined
                  }
                >
                  <RestaurantWalletListMini
                    organizationId={orgId}
                    ref={walletsRef}
                    onWalletListChanged={(w) => setWalletsCount(w?.length ?? 0)}
                  />
                </RestaurantSection>
              )}
              {hasRW && (
                <RestaurantSection
                  title="Transactions"
                  seeAll={
                    transactionCount > 2
                      ? {
                          href: {
                            pathname:
                              "/(tabs)/orgs/[orgId]/transactions",
                            params: { orgId },
                          },
                        }
                      : undefined
                  }
                >
                  <RestaurantTransactionListMini
                    organizationId={orgId}
                    ref={transactionRef}
                    onListChanged={(t) => setTransactionCount(t?.length ?? 0)}
                  />
                </RestaurantSection>
              )}
              {hasRW && (
                <RestaurantSection
                  title="Team"
                  seeAll={{
                    text: "Manage",
                    href: {
                      pathname: "/(tabs)/orgs/[orgId]/team",
                      params: { orgId },
                    },
                  }}
                >
                  <RestaurantTeamListMini
                    organizationId={orgId}
                    ref={teamListRef}
                    onListChanged={(t) => setTeammatesCount(t?.length ?? 0)}
                  />
                </RestaurantSection>
              )}
              <RestaurantSection title="Settings">
                <VStack className="gap-2">
                  {hasRW ? (
                    <>
                      <ExpoLink
                        href={{
                          pathname: "/(tabs)/orgs/[orgId]/qrcodes",
                          params: { orgId },
                        }}
                        push
                      >
                        <RestaurantSettingsItem
                          icon={QrCode}
                          title="QR codes"
                          subtitle="Manage QR codes for this business"
                        />
                      </ExpoLink>
                      <Divider className="my-1" />
                      <ExpoLink
                        href={{
                          pathname: "/(tabs)/orgs/[orgId]/tipssharing",
                          params: { orgId },
                        }}
                        push
                      >
                        <RestaurantSettingsItem
                          icon={HandCoins}
                          title="Tips sharing scheme"
                          subtitle="Manage tips distribution across team"
                        />
                      </ExpoLink>
                    </>
                  ) : !!myParticipant && !!user ? (
                    <>
                      <RestaurantTeamModalDelete
                        organizationId={orgId}
                        participant={{
                          ...myParticipant,
                          user: user,
                        }}
                        isOpen={deleteIsOpened}
                        onClose={() => setDeleteIsOpened(false)}
                        onDelete={() => router.replace("/(tabs)/orgs")}
                      />
                      <Pressable onPress={() => setDeleteIsOpened(true)}>
                        <RestaurantSettingsItem
                          icon={DoorOpen}
                          title="Leave"
                          subtitle="Leave this team"
                          type="DESTRUCTIVE"
                        />
                      </Pressable>
                    </>
                  ) : null}
                </VStack>
              </RestaurantSection>
            </VStack>
          ) : isLoadingError ? (
            <EmptyState
              title="Something went wrong"
              description="Please check your internet connection and try again"
            />
          ) : (
            <Spinner size="large" />
          )}
        </ScrollView>
      </Box>
    </>
  );
}
